import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { I18nProvider } from "@/context/I18nContext";
import { CookieConsent } from "@/components/CookieConsent";
import { FloatingSupport } from "@/components/FloatingSupport";

import LandingPage from "@/pages/LandingPage";
import ModuleDetailPage from "@/pages/ModuleDetailPage";
import PricingPage from "@/pages/PricingPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import SupportPage from "@/pages/SupportPage";
import PrivacyPage from "@/pages/legal/PrivacyPage";
import TermsPage from "@/pages/legal/TermsPage";
import LegalNoticePage from "@/pages/legal/LegalNoticePage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import InvitationAcceptPage from "@/pages/InvitationAcceptPage";
import DashboardLayout from "@/pages/dashboard/DashboardLayout";
import DashboardHome from "@/pages/dashboard/DashboardHome";
import ModulesPage from "@/pages/dashboard/ModulesPage";
import ActivityPage from "@/pages/dashboard/ActivityPage";
import NotificationsPage from "@/pages/dashboard/NotificationsPage";
import TeamPage from "@/pages/dashboard/TeamPage";
import SettingsPage from "@/pages/dashboard/SettingsPage";
import CloudConnectPage from "@/pages/dashboard/CloudConnectPage";
import DocumentsPage from "@/pages/dashboard/DocumentsPage";
import MsActivationsAdminPage from "@/pages/dashboard/MsActivationsAdminPage";
import BillingPage from "@/pages/dashboard/BillingPage";
import MediaKitPage from "@/pages/dashboard/MediaKitPage";
import SupportTicketsPage from "@/pages/dashboard/SupportTicketsPage";
import VersionsAdminPage from "@/pages/dashboard/VersionsAdminPage";
import CreatorCenterPage from "@/pages/dashboard/CreatorCenterPage";
import MasterCenterPage from "@/pages/dashboard/MasterCenterPage";
import TechnicianCenterPage from "@/pages/dashboard/TechnicianCenterPage";
import PlatformInvitationPage from "@/pages/PlatformInvitationPage";
import InternalLoginPage from "@/pages/InternalLoginPage";
import NouveautesPage from "@/pages/NouveautesPage";

function Loader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#050505]">
      <div className="flex flex-col items-center gap-4">
        <div className="w-10 h-10 rounded-full border-2 border-[#EAB308]/30 border-t-[#EAB308] animate-spin" />
        <span className="text-sm text-white/40 font-mono uppercase tracking-widest">Chargement…</span>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading || user === null) return <Loader />;
  if (!user) {
    const dest = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?redirect=${encodeURIComponent(dest)}`} replace />;
  }
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading || user === null) return <Loader />;
  if (user) {
    const params = new URLSearchParams(location.search);
    const redirect = params.get("redirect");
    return <Navigate to={redirect || "/app"} replace />;
  }
  return children;
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
      <ThemeProvider>
        <I18nProvider>
        <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/module/:key" element={<ModuleDetailPage />} />
            <Route path="/tarification" element={<PricingPage />} />
            <Route path="/paiement/succes" element={<PaymentSuccessPage />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/nouveautes" element={<NouveautesPage />} />
            <Route path="/confidentialite" element={<PrivacyPage />} />
            <Route path="/conditions" element={<TermsPage />} />
            <Route path="/mentions-legales" element={<LegalNoticePage />} />
            <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
            <Route path="/register" element={<PublicOnly><RegisterPage /></PublicOnly>} />
            <Route path="/mot-de-passe-oublie" element={<ForgotPasswordPage />} />
            <Route path="/reinitialiser" element={<ResetPasswordPage />} />
            <Route path="/invitation" element={<InvitationAcceptPage />} />
            <Route path="/interne/invitation" element={<PlatformInvitationPage />} />
            <Route path="/interne" element={<InternalLoginPage />} />
            <Route
              path="/app"
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<DashboardHome />} />
              <Route path="modules" element={<ModulesPage />} />
              <Route path="activity" element={<ActivityPage />} />
              <Route path="notifications" element={<NotificationsPage />} />
              <Route path="team" element={<TeamPage />} />
              <Route path="support" element={<SupportTicketsPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="cloud-connect" element={<CloudConnectPage />} />
              <Route path="documents" element={<DocumentsPage />} />
              <Route path="billing" element={<BillingPage />} />
              <Route path="media-kit" element={<MediaKitPage />} />
              <Route path="versions" element={<VersionsAdminPage />} />
              <Route path="centre-createur" element={<CreatorCenterPage />} />
              <Route path="master" element={<MasterCenterPage />} />
              <Route path="master/cloud-connect" element={<MsActivationsAdminPage />} />
              <Route path="master/:module" element={<MasterCenterPage />} />
              <Route path="centre-technicien" element={<TechnicianCenterPage />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="bottom-right" theme="dark" richColors />
          <CookieConsent />
          <FloatingSupport />
        </BrowserRouter>
      </AuthProvider>
        </I18nProvider>
      </ThemeProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
